import React, {useEffect, useState} from "react";
import {useHistory, useParams} from 'react-router-dom';
import {Controller, useForm} from 'react-hook-form'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Text,
    useColorModeValue,
    useToast,
    Checkbox,
    Heading,
    Badge,
    Progress,
    NumberInputField,
    NumberInput,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    InputLeftAddon,
    InputGroup,
    Fade,
    SlideFade,
    SimpleGrid,
    Icon,
    ListItem, ListIcon, List, Link, Spinner, useDisclosure, ButtonGroup
} from "@chakra-ui/react";
import Card from "../../../components/card/Card";
import {getData, postData, putData} from "../../../services/api";
import {rentalTypes} from "../../../variables/rentalTypes";
import {amenityTypes} from "../../../variables/amenityTypes";
import CharacterCounter from "../../../components/counter/CharacterCounter";
import Mapbox from "../../../components/maps/Mapbox";
import FileDropAndSort from "../../../components/dragndrop/FileDropAndSort.js";
import mapboxgl from "mapbox-gl";
import MapboxMoveable from "../../../components/maps/MapboxMoveable";
import {FaBath, FaTools} from "react-icons/fa";
import {IoBed, IoPeople, IoEye, IoSave} from "react-icons/io5";
import MyCKEditor from "../../../components/editor/MyCKEditor";
import {MdCheckCircle} from "react-icons/md";
import RentalPreview from "../../../components/rental/RentalPreview";
import PlaceSearch from "../../../components/googleplaces/PlaceSearch";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

export default function RentalAdd() {
    const {id} = useParams();
    const MySwal = withReactContent(Swal)

    const textColor = useColorModeValue("navy.700", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const toast = useToast()
    const history = useHistory();

    const {isOpen, onOpen, onClose} = useDisclosure();

    const [start, setStart] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isFading, setIsFading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedAmenities, setSelectedAmenities] = useState([]);
    const [previewData, setPreviewData] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [address, setAddress] = useState(null);
    const [coordinates, setCoordinates] = useState(null);
    const [user, setUser] = useState(null);
    const [hasGoogleReviews, setHasGoogleReviews] = useState(null);
    const [googlePlaceId, setGooglePlaceId] = useState(null);

    const {
        setValue,
        getValues,
        control,
        handleSubmit,
        register,
        reset,
        watch,
        formState: {errors, isSubmitting},
    } = useForm({
        defaultValues: {
            price: 10,
            bathrooms: 1,
            bedrooms: 1,
            size: 10,
            guests: 2,
        }
    })

    const steps = [
        'STEP_TYPES',
        'STEP_FILES',
        'STEP_TITLE',
        'STEP_AMENITIES',
        'STEP_INFORMATIONS',
        'STEP_RATING',
        'STEP_LOCATION',
        'STEP_COORDINATES',
        'STEP_OPTIN',
    ]

    useEffect(() => {
        const getUserData = async () => {
            try {
                const result = await getData('/api/user/info');
                setUser(result);

                if (!result.has_address) {
                    history.push(`/user/dashboard`);
                }

            } catch (error) {
                toast({
                    title: "Fehler.",
                    description: error.message,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };
        getUserData();

        const getRentalData = async () => {
            setIsLoading(true);
            try {
                const result = await getData(`/api/rentals/${id}`);
                reset(result.data);
                setSelectedTypes(result.data?.types)
                setSelectedAmenities(result.data?.amenities)
                setSelectedFiles(result.data?.pictures)
                setStepByName(result.data?.wizard_step)
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        };
        getRentalData();
    }, []);

    const setStepByName = (stepName) => {
        const stepIndex = steps.indexOf(stepName);
        if (stepIndex !== -1) {
            setStart(true)
            setCurrentStep(stepIndex);
            // setCurrentStep(stepIndex < steps.length ? stepIndex + 1 : stepIndex);
        }
    };

    const handleAddressChange = (address) => {
        setValue('street', address?.street);
        setValue('zip', address?.postcode);
        setValue('place', address?.place);
        setValue('country', address?.country);
        setAddress(address)
    };

    const handleCoordinatesChange = (coordinates) => {
        setCoordinates(coordinates)
    };

    const saveData = async (data) => {
        data.pictures = selectedFiles;
        data.types = selectedTypes;
        data.amenities = selectedAmenities;
        data.lat = coordinates?.lat;
        data.lng = coordinates?.lng;
        data.wizard_step = steps[currentStep];
        data.google_place_id = googlePlaceId;

        try {
            await putData('/api/rentals/' + id, data);
            return true;
        } catch (error) {
            toast({
                title: 'Es ist ein Fehler aufgetreten: ' + error.message,
                status: 'error',
                isClosable: true,
            })
            setIsLoading(false);
            return false;
        }
    };

    const handleNext = async () => {

        setIsFading(false);
        setIsLoading(true);

        const currentData = getValues();
        const success = await saveData(currentData);

        if (success) {
            setCurrentStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
            setIsLoading(false);
            setIsFading(true);
            window.scrollTo({top: 0, behavior: 'smooth'});
        } else {
            setIsLoading(false);
            setIsFading(true);
        }
    };

    const handlePrevious = async () => {

        setIsFading(false);
        setIsLoading(true);

        const currentData = getValues();
        const success = await saveData(currentData);

        if (success) {
            setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
            setIsLoading(false);
            setIsFading(true);
            window.scrollTo({top: 0, behavior: 'smooth'});

            if (currentStep === 6) {
                handleAddressChange(null);
            }
        } else {
            setIsLoading(false);
            setIsFading(true); // Reset fade-in without advancing
        }
    };

    const handleOpenPreview = () => {
        setPreviewData({
            'title': watch("title"),
            'description': watch("description"),
            'bedrooms': watch("bedrooms"),
            'guests': watch("guests"),
            'size': watch("size"),
            'price': watch("price"),
            'place': watch("place"),
            'picture': selectedFiles[0],
        })
        onOpen()
    };

    const handleExit = async () => {
        setIsLoading(true);

        const currentData = getValues();
        const success = await saveData(currentData);
        if (success) {
            setIsLoading(false);
            history.push(`/user/default`);
        }
    };

    const handleFilesSelected = (files) => {
        setSelectedFiles(files);
    };

    const handleSelectAmenity = (amenityId) => {
        setSelectedAmenities(prevSelected => {
            if (prevSelected.includes(amenityId)) {
                return prevSelected.filter(id => id !== amenityId);
            } else {
                return [...prevSelected, amenityId];
            }
        });
    };

    const handleSelectType = (typeId) => {
        setSelectedTypes(prevSelected => {
            if (prevSelected.includes(typeId)) {
                return prevSelected.filter(id => id !== typeId);
            } else {
                return [...prevSelected, typeId];
            }
        });
    };

    const handleGooglePlaceSelected = (place) => {
        setGooglePlaceId(place?.place_id)
    }

    const isValidTitle = watch('title')?.replace(/<[^>]*>/g, '').length >= 1 && watch('title')?.replace(/<[^>]*>/g, '').length <= 60;
    const isValidDescription = watch('description')?.replace(/<[^>]*>/g, '').length >= 1 && watch('description')?.replace(/<[^>]*>/g, '').length <= 3000;

    const validateCurrentStep = () => {
        switch (currentStep) {
            case 0:
                return selectedTypes?.length >= 1
            case 1:
                return selectedFiles.length >= 5
            case 2:
                return isValidTitle && isValidDescription;
            case 3:
                return selectedAmenities.length >= 1
            case 4:
                // return watch('cancellation')
                return true
            case 5:
                return hasGoogleReviews === false || ( hasGoogleReviews && googlePlaceId != null)
            case 6:
                return watch('street') && watch('place')
            case 7:
                return coordinates
            case 8:
                return watch('optin')
            default:
                return false;
        }
    };

    const stepIsValid = validateCurrentStep();

    const handleUrlChange = (fieldName) => (e) => {
        let value = e.target.value;

        if (value.trim() === "" || value.trim() === "https://") {
            setValue(fieldName, "", {shouldValidate: true});
            return true;
        }

        try {
            const modifiedValue = !/^https?:\/\//i.test(value) ? `https://${value}` : value;
            setValue(fieldName, modifiedValue, {shouldValidate: true});
            new URL(modifiedValue);
            return true;
        } catch (error) {
            return "Please enter a valid URL";
        }
    };

    const formatAddress = () => {
        const street = watch('street');
        const zip = watch('zip');
        const place = watch('place');
        const country = watch('country');

        return [street, `${zip} ${place}`, country].filter(Boolean).join(', ');
    };
    const formattedAddress = formatAddress();

    async function onSubmit(values) {

        values.wizard_finalized = true;
        const success = await saveData(values);

        if (success) {
            MySwal.fire({
                title: <p>Inserat wurde erstellt</p>,
                html: <p>Das Inserat wurde erfolgreich erstellt. Wir haben dir hierzu eine E-Mail mit der
                    Bestätigung
                    und der Rechnung gesendet.<br/><br/><strong>Bitte beachte dass das Inserat erst nach
                        Begleichung der Rechnung aktiviert wird.</strong></p>,
                confirmButtonText: 'Weiter zum Inserat',
                icon: 'success',
            }).then((result) => {
                if (result.isConfirmed) {
                    history.push(`/user/rental/${id}`);
                }
            })
        } else {
            toast({
                title: 'Inserat konnte nicht erstellt werden',
                status: 'error',
                isClosable: true,
            })
        }
    }

    const handleOptimize = async () => {
        const description = getValues('description');

        setIsLoading(true);
        try {
            const { optimized_text } = await postData('/api/ai/optimize', {
                text: description,
            });
            setValue('description', optimized_text);
        } catch (error) {
            toast({
                title: 'Es ist ein Fehler aufgetreten: ' + error.message,
                status: 'error',
                isClosable: true,
            })
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box pt={"80px"}>
            <style>
                {`
                    .form-error input{
                        border-color:red!important;
                    }
                    .form-error input:not(:focus){
                        background-color:rgba(255, 0, 0, 0.1);
                    }
                `}
            </style>
            {!start ?
                <Fade in={true}>
                    {!isLoading ?
                        <Card>
                            <Flex direction={{base: "column", lg: 'row'}}>
                                <Heading px={5} size='2xl' mb={{base: 5, lg: 0}}>
                                    Es ist ganz einfach, deine Unterkunft bei uns anzulegen.
                                </Heading>
                                <Box>
                                    <Flex mb={5}>
                                        <div><Badge fontSize='16px'>1</Badge></div>
                                        <Text ml={1}><strong>Beschreibe deine Unterkunft</strong><br/>Gib uns einige
                                            wesentliche Details zu Deiner Unterkunft und füge einen aussagekräftigen
                                            Titel
                                            sowie eine detaillierte Beschreibung hinzu. Je präziser und ansprechender
                                            deine
                                            Unterkunft beschrieben ist, desto höher sind die Chancen auf mehr
                                            Buchungen.</Text>
                                    </Flex>
                                    <Flex mb={5}>
                                        <div><Badge fontSize='16px'>2</Badge></div>
                                        <Text ml={1}><strong>Stich aus der Masse heraus</strong><br/>Lade mindestens
                                            fünf
                                            Fotos hoch und füge die wichtigsten Merkmale deiner Unterkunft hinzu.
                                            Ergänze
                                            den Link zu deiner Webseite und weitere wichtige Angaben.</Text>
                                    </Flex>
                                    <Flex>
                                        <div><Badge fontSize='16px'>3</Badge></div>
                                        <Text ml={1}><strong>Vervollständige dein Inserat und veröffentliche
                                            es</strong><br/>Gib den genauen Ort deiner Unterkunft an und schalte dein
                                            Inserat anschließend live.</Text>
                                    </Flex>
                                </Box>
                            </Flex>
                            <Button
                                fontSize='sm'
                                variant='brand'
                                fontWeight='500'
                                w='100px'
                                h='50'
                                mt='24px'
                                ml='auto'
                                onClick={() => setStart(true)}
                            >
                                Loslegen
                            </Button>
                        </Card> :
                        <Card display="flex" justifyContent="center" alignItems="center">
                            <Flex direction="column" align="center">
                                <Heading px={5} size="lg" mb={5}>
                                    <Icon
                                        w='24px'
                                        h='24px'
                                        me='5px'
                                        as={FaTools}
                                    /> Editor wird geladen
                                </Heading>
                                <Spinner size="lg"/>
                            </Flex>
                        </Card>
                    }
                </Fade> :
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box pb={{base: 5, lg: '100px'}}>
                        {
                            currentStep === 0 ?
                                <SlideFade in={isFading} offsetY='20px'>
                                    <Card>
                                        <Heading mb={5} size='xl'>Welche der folgenden Aussagen beschreibt deine
                                            Unterkunft am besten?</Heading>
                                        <SimpleGrid columns={{base: 1, md: 3}} gap='20px'>
                                            {rentalTypes.map((type) => (
                                                <Box
                                                    cursor="pointer"
                                                    onClick={() => handleSelectType(type.id)}
                                                    p={5}
                                                    borderColor={selectedTypes.includes(type.id) ? 'brand.500' : 'gray.300'}
                                                    borderWidth='1px'
                                                    borderRadius='lg'
                                                    key={type.id}
                                                    value={type.id}>
                                                    {type.name}
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                        <FormErrorMessage>
                                            {errors.type && errors.type.message}
                                        </FormErrorMessage>
                                    </Card>
                                </SlideFade> :
                                currentStep === 1 ?
                                    <SlideFade in={isFading} offsetY='20px'>
                                        <FileDropAndSort rentalId={id} files={selectedFiles}
                                                         setFiles={handleFilesSelected}/>
                                    </SlideFade> :
                                    currentStep === 2 ?
                                        <SlideFade in={isFading} offsetY='20px'>
                                            <Card>
                                                <Heading mb={5} size='xl'>Wie würdest du deine Unterkunft
                                                    beschreiben?</Heading>
                                                <FormControl mb='24px' className={watch('title') && !isValidTitle ? "form-error" : ""}>
                                                    <FormLabel
                                                        ms='4px'
                                                        fontSize='sm'
                                                        fontWeight='500'
                                                        color={textColor}
                                                        htmlFor='title'
                                                    >
                                                        <Heading size="md" mb={2}>Titel*</Heading>
                                                        <Text mb={2}>Kurze Titel erzielen die besten Ergebnisse.
                                                            Keine Sorge, du kannst den Titel jederzeit nachträglich
                                                            anpassen.</Text>
                                                    </FormLabel>
                                                    <Input
                                                        id='title'
                                                        fontSize='sm'
                                                        type='text'
                                                        fontWeight='500'
                                                        size='lg'
                                                        {...register('title')}
                                                    />
                                                    <CharacterCounter maxLength={60} text={watch('title')}/>
                                                </FormControl>
                                                <FormControl mb='24px'
                                                             className={watch('description') && !isValidDescription ? "form-error" : ""}>
                                                    <FormLabel
                                                        ms='4px'
                                                        fontSize='sm'
                                                        fontWeight='500'
                                                        color={textColor}
                                                        htmlFor='description'
                                                    >
                                                        <Heading size="md" mb={2}>Beschreibung*</Heading>
                                                        <Text mb={2}>Erkläre, was deine Unterkunft einzigartig
                                                            macht.</Text>
                                                    </FormLabel>
                                                    <Controller
                                                        name="description"
                                                        control={control}
                                                        defaultValue=""
                                                        render={({field}) => (
                                                            <div>
                                                                <MyCKEditor field={field}/>
                                                            </div>
                                                        )}
                                                    />
                                                    <CharacterCounter maxLength={3000} text={watch('description')}/>
                                                    <Button
                                                        mt={3}
                                                        onClick={handleOptimize}
                                                        isLoading={isLoading}
                                                        loadingText="Optimieren..."
                                                        colorScheme="teal"
                                                        leftIcon={<span role="img" aria-label="magic">✨</span>} // Zauberstab-Icon
                                                    >
                                                        Text von KI optimieren lassen
                                                    </Button>
                                                </FormControl>
                                            </Card>
                                        </SlideFade> :
                                        currentStep === 3 ?
                                            <SlideFade in={isFading} offsetY='20px'>
                                                <Card>
                                                    <Heading mb={5} size='xl'>Was hat deine Unterkunft zu
                                                        bieten?</Heading>
                                                    <SimpleGrid columns={{base: 1, md: 3}} gap='20px'>
                                                        {amenityTypes.map((amenity) => (
                                                            <Box cursor="pointer"
                                                                 onClick={() => handleSelectAmenity(amenity.id)}
                                                                 p={5}
                                                                 borderColor={selectedAmenities.includes(amenity.id) ? 'brand.500' : 'gray.300'}
                                                                 borderWidth='1px'
                                                                 borderRadius='lg'
                                                                 key={amenity.id}
                                                            >
                                                                {amenity.name}
                                                            </Box>
                                                        ))}
                                                    </SimpleGrid>
                                                </Card>
                                            </SlideFade> :
                                            currentStep === 4 ?
                                                <SlideFade in={isFading} offsetY='20px'>
                                                    <Card>
                                                        <Heading width={'75%'} mb={5} size='xl'>Mach einige
                                                            grundlegende
                                                            Angaben
                                                            zu
                                                            deiner
                                                            Unterkunft</Heading>
                                                        <FormControl mb='24px'>
                                                            <FormLabel
                                                                display='flex'
                                                                ms='4px'
                                                                fontSize='sm'
                                                                fontWeight='500'
                                                                color={textColor}
                                                                htmlFor='price'
                                                            >
                                                                Preis pro Übernachtung ab<Text
                                                                color={brandStars}>*</Text>
                                                            </FormLabel>
                                                            <InputGroup>
                                                                <InputLeftAddon height='auto'>€</InputLeftAddon>
                                                                <Controller
                                                                    name="price"
                                                                    control={control}
                                                                    render={({field}) => (
                                                                        <NumberInput
                                                                            size='lg'
                                                                            maxW={32}
                                                                            defaultValue={10}
                                                                            min={1}
                                                                            value={field.value}
                                                                            onChange={(valueAsString, valueAsNumber) => {
                                                                                setValue('price', valueAsNumber);
                                                                            }}
                                                                        >
                                                                            <NumberInputField
                                                                                id='price'
                                                                                fontSize='sm'
                                                                                type='number'
                                                                                fontWeight='500'
                                                                                size='lg'
                                                                                borderLeftRadius={0}
                                                                            />
                                                                            <NumberInputStepper>
                                                                                <NumberIncrementStepper/>
                                                                                <NumberDecrementStepper/>
                                                                            </NumberInputStepper>
                                                                        </NumberInput>
                                                                    )}
                                                                />
                                                            </InputGroup>
                                                            <FormErrorMessage>
                                                                {errors.price && errors.price.message}
                                                            </FormErrorMessage>
                                                        </FormControl>
                                                        <Flex justifyContent='space-between' gap='15px'
                                                              align='center'
                                                              mb='24px' direction={{base: "column", lg: 'row'}}>
                                                            <FormControl>
                                                                <FormLabel
                                                                    display='flex'
                                                                    ms='4px'
                                                                    fontSize='sm'
                                                                    fontWeight='500'
                                                                    color={textColor}
                                                                    htmlFor='bedrooms'
                                                                >
                                                                    Anzahl Schlafzimmer<Text
                                                                    color={brandStars}>*</Text>
                                                                </FormLabel>
                                                                <InputGroup>
                                                                    <InputLeftAddon height='auto'>
                                                                        <Icon
                                                                            transition='0.2s linear'
                                                                            w='20px'
                                                                            h='20px'
                                                                            as={IoBed}
                                                                        />
                                                                    </InputLeftAddon>
                                                                    <Controller
                                                                        name="bedrooms"
                                                                        control={control}
                                                                        render={({field}) => (
                                                                            <NumberInput
                                                                                size='lg'
                                                                                maxW={32}
                                                                                defaultValue={1}
                                                                                min={1}
                                                                                value={field.value}
                                                                                onChange={(valueAsString, valueAsNumber) => {
                                                                                    setValue('bedrooms', valueAsNumber);
                                                                                }}
                                                                            >
                                                                                <NumberInputField
                                                                                    id='bedrooms'
                                                                                    fontSize='sm'
                                                                                    type='number'
                                                                                    fontWeight='500'
                                                                                    size='lg'
                                                                                    borderLeftRadius={0}
                                                                                />
                                                                                <NumberInputStepper>
                                                                                    <NumberIncrementStepper/>
                                                                                    <NumberDecrementStepper/>
                                                                                </NumberInputStepper>
                                                                            </NumberInput>
                                                                        )}
                                                                    />
                                                                </InputGroup>
                                                                <FormErrorMessage>
                                                                    {errors.bedrooms && errors.bedrooms.message}
                                                                </FormErrorMessage>
                                                            </FormControl>
                                                            <FormControl>
                                                                <FormLabel
                                                                    display='flex'
                                                                    ms='4px'
                                                                    fontSize='sm'
                                                                    fontWeight='500'
                                                                    color={textColor}
                                                                    htmlFor='bathrooms'
                                                                >
                                                                    Anzahl Badezimmer<Text
                                                                    color={brandStars}>*</Text>
                                                                </FormLabel>
                                                                <InputGroup>
                                                                    <InputLeftAddon height='auto'>
                                                                        <Icon
                                                                            transition='0.2s linear'
                                                                            w='20px'
                                                                            h='20px'
                                                                            as={FaBath}
                                                                        />
                                                                    </InputLeftAddon>
                                                                    <Controller
                                                                        name="bathrooms"
                                                                        control={control}
                                                                        render={({field}) => (
                                                                            <NumberInput
                                                                                size='lg'
                                                                                maxW={32}
                                                                                defaultValue={1}
                                                                                min={1}
                                                                                value={field.value}
                                                                                onChange={(valueAsString, valueAsNumber) => {
                                                                                    setValue('bathrooms', valueAsNumber);
                                                                                }}
                                                                            >
                                                                                <NumberInputField
                                                                                    id='bathrooms'
                                                                                    fontSize='sm'
                                                                                    type='number'
                                                                                    fontWeight='500'
                                                                                    size='lg'
                                                                                    borderLeftRadius={0}
                                                                                />
                                                                                <NumberInputStepper>
                                                                                    <NumberIncrementStepper/>
                                                                                    <NumberDecrementStepper/>
                                                                                </NumberInputStepper>
                                                                            </NumberInput>
                                                                        )}
                                                                    />
                                                                </InputGroup>
                                                                <FormErrorMessage>
                                                                    {errors.bathrooms && errors.bathrooms.message}
                                                                </FormErrorMessage>
                                                            </FormControl>
                                                        </Flex>
                                                        <Flex justifyContent='space-between' gap='15px'
                                                              align='center'
                                                              direction={{base: "column", lg: 'row'}}>
                                                            <FormControl>
                                                                <FormLabel
                                                                    display='flex'
                                                                    ms='4px'
                                                                    fontSize='sm'
                                                                    fontWeight='500'
                                                                    color={textColor}
                                                                    htmlFor='guests'
                                                                >
                                                                    Max. Anzahl Personen<Text
                                                                    color={brandStars}>*</Text>
                                                                </FormLabel>
                                                                <InputGroup>
                                                                    <InputLeftAddon height='auto'>
                                                                        <Icon
                                                                            transition='0.2s linear'
                                                                            w='20px'
                                                                            h='20px'
                                                                            as={IoPeople}
                                                                        />
                                                                    </InputLeftAddon>
                                                                    <Controller
                                                                        name="guests"
                                                                        control={control}
                                                                        render={({field}) => (
                                                                            <NumberInput
                                                                                size='lg'
                                                                                maxW={32}
                                                                                defaultValue={1}
                                                                                min={1}
                                                                                value={field.value}
                                                                                onChange={(valueAsString, valueAsNumber) => {
                                                                                    setValue('guests', valueAsNumber);
                                                                                }}
                                                                            >
                                                                                <NumberInputField
                                                                                    id='guests'
                                                                                    fontSize='sm'
                                                                                    type='number'
                                                                                    fontWeight='500'
                                                                                    size='lg'
                                                                                    borderLeftRadius={0}
                                                                                />
                                                                                <NumberInputStepper>
                                                                                    <NumberIncrementStepper/>
                                                                                    <NumberDecrementStepper/>
                                                                                </NumberInputStepper>
                                                                            </NumberInput>
                                                                        )}
                                                                    />
                                                                </InputGroup>
                                                                <FormErrorMessage>
                                                                    {errors.guests && errors.guests.message}
                                                                </FormErrorMessage>
                                                            </FormControl>
                                                            <FormControl>
                                                                <FormLabel
                                                                    display='flex'
                                                                    ms='4px'
                                                                    fontSize='sm'
                                                                    fontWeight='500'
                                                                    color={textColor}
                                                                    htmlFor='size'
                                                                >
                                                                    Größe Unterkunft<Text
                                                                    color={brandStars}>*</Text>
                                                                </FormLabel>
                                                                <InputGroup>
                                                                    <InputLeftAddon
                                                                        height='auto'>qm</InputLeftAddon>
                                                                    <Controller
                                                                        name="size"
                                                                        control={control}
                                                                        render={({field}) => (
                                                                            <NumberInput
                                                                                size='lg'
                                                                                maxW={32}
                                                                                defaultValue={10}
                                                                                min={1}
                                                                                value={field.value}
                                                                                onChange={(valueAsString, valueAsNumber) => {
                                                                                    setValue('size', valueAsNumber);
                                                                                }}
                                                                            >
                                                                                <NumberInputField
                                                                                    id='size'
                                                                                    fontSize='sm'
                                                                                    type='number'
                                                                                    fontWeight='500'
                                                                                    size='lg'
                                                                                    borderLeftRadius={0}
                                                                                />
                                                                                <NumberInputStepper>
                                                                                    <NumberIncrementStepper/>
                                                                                    <NumberDecrementStepper/>
                                                                                </NumberInputStepper>
                                                                            </NumberInput>
                                                                        )}
                                                                    />
                                                                </InputGroup>
                                                                <FormErrorMessage>
                                                                    {errors.size && errors.size.message}
                                                                </FormErrorMessage>
                                                            </FormControl>
                                                        </Flex>
                                                    </Card>
                                                    <Card mt={5}>
                                                        <Heading size='sm' mb={5}>Wichtige Informationen</Heading>
                                                        <FormControl mb='24px'>
                                                            <FormLabel
                                                                display='flex'
                                                                ms='4px'
                                                                fontSize='sm'
                                                                fontWeight='500'
                                                                color={textColor}
                                                                htmlFor='additional_information'
                                                            >
                                                                z.B. Mindestaufenhalt, Stornobedingungen, Endreinigung-Gebühr etc.
                                                            </FormLabel>
                                                            {/*<Textarea*/}
                                                            {/*    id='additional_information'*/}
                                                            {/*    fontSize='sm'*/}
                                                            {/*    fontWeight='500'*/}
                                                            {/*    size='lg'*/}
                                                            {/*/>*/}
                                                            <Controller
                                                                name="additional_information"
                                                                control={control}
                                                                defaultValue=""
                                                                render={({field}) => (
                                                                    <MyCKEditor
                                                                        field={field}
                                                                    />
                                                                )}
                                                            />
                                                        </FormControl>
                                                    </Card>
                                                    <Card mt={5}>
                                                        <Heading size='sm' mb={5}>Optionale Angaben</Heading>
                                                        <FormControl mb='24px'>
                                                            <FormLabel
                                                                display='flex'
                                                                ms='4px'
                                                                fontSize='sm'
                                                                fontWeight='500'
                                                                color={textColor}
                                                                htmlFor='link'
                                                            >
                                                                Webseite
                                                            </FormLabel>
                                                            <Input
                                                                id='link'
                                                                fontSize='sm'
                                                                type='text'
                                                                fontWeight='500'
                                                                size='lg'
                                                                placeholder="Link zur Webseite"
                                                                {...register('link', {
                                                                    onChange: handleUrlChange('link'),
                                                                    pattern: {
                                                                        value: /^(ftp|http|https):\/\/[^ "]+$/,
                                                                        message: 'Bitte geben eine gültige URL an'
                                                                    }
                                                                })}
                                                            />
                                                            <FormErrorMessage>
                                                                {errors.link && errors.link.message}
                                                            </FormErrorMessage>
                                                        </FormControl>
                                                        <FormControl mb='24px'>
                                                            <FormLabel
                                                                display='flex'
                                                                ms='4px'
                                                                fontSize='sm'
                                                                fontWeight='500'
                                                                color={textColor}
                                                                htmlFor='facebook'
                                                            >
                                                                Facebook
                                                            </FormLabel>
                                                            <Input
                                                                id='link'
                                                                fontSize='sm'
                                                                type='text'
                                                                fontWeight='500'
                                                                size='lg'
                                                                placeholder="Link zu Facebook"
                                                                {...register('facebook', {
                                                                    onChange: handleUrlChange('facebook'),
                                                                    pattern: {
                                                                        value: /^(ftp|http|https):\/\/[^ "]+$/,
                                                                        message: 'Bitte geben eine gültige URL an'
                                                                    }
                                                                })}
                                                            />
                                                        </FormControl>
                                                        <FormControl mb='24px'>
                                                            <FormLabel
                                                                display='flex'
                                                                ms='4px'
                                                                fontSize='sm'
                                                                fontWeight='500'
                                                                color={textColor}
                                                                htmlFor='instagram'
                                                            >
                                                                Instagram
                                                            </FormLabel>
                                                            <Input
                                                                id='link'
                                                                fontSize='sm'
                                                                type='text'
                                                                fontWeight='500'
                                                                size='lg'
                                                                placeholder="Link zu Instagram"
                                                                {...register('instagram', {
                                                                    onChange: handleUrlChange('instagram'),
                                                                    pattern: {
                                                                        value: /^(ftp|http|https):\/\/[^ "]+$/,
                                                                        message: 'Bitte gebe eine gültige URL an'
                                                                    }
                                                                })}
                                                            />
                                                        </FormControl>
                                                    </Card>
                                                </SlideFade> :
                                                currentStep === 5 ?
                                                    <SlideFade in={isFading} offsetY='20px'>
                                                        <Card>
                                                            <Heading mb={5} size='xl'>Möchtest du Bewertungen von Google anzeigen?</Heading>
                                                            <Box>
                                                                <ButtonGroup variant='outline' spacing='6' mb={5}>
                                                                    <Button onClick={() => setHasGoogleReviews(true)} colorScheme={hasGoogleReviews ? 'blue' : null}>Ja</Button>
                                                                    <Button onClick={() => setHasGoogleReviews(false)} colorScheme={!hasGoogleReviews ? 'blue' : null}>Nein</Button>
                                                                </ButtonGroup>
                                                                {hasGoogleReviews &&
                                                                    <Box>
                                                                        <Text mb={3}>Suche hier nach deinem Objekt und wähle den passenden Eintrag:</Text>
                                                                        <PlaceSearch onPlaceSelected={handleGooglePlaceSelected}/>
                                                                    </Box>
                                                                }
                                                            </Box>
                                                        </Card>
                                                    </SlideFade> :
                                                currentStep === 6 ?
                                                    <SlideFade in={isFading} offsetY='20px'>
                                                        <Card>
                                                            <Heading mb={5} size='xl'>Wo befindet sich deine
                                                                Unterkunft?</Heading>
                                                            {address ?
                                                                <Box>
                                                                    <FormControl mb='5' isInvalid={errors.country}
                                                                                 display={'none'}>
                                                                        <FormLabel
                                                                            display='flex'
                                                                            ms='4px'
                                                                            fontSize='sm'
                                                                            fontWeight='500'
                                                                            color={textColor}
                                                                            htmlFor='country'
                                                                        >
                                                                            Land<Text color={brandStars}>*</Text>
                                                                        </FormLabel>
                                                                        <Input
                                                                            id='country'
                                                                            fontSize='sm'
                                                                            type='text'
                                                                            fontWeight='500'
                                                                            size='lg'
                                                                            {...register('country', {
                                                                                required: 'Bitte geben Sie das Land der Unterkunft an',
                                                                            })}
                                                                        />
                                                                        <FormErrorMessage>
                                                                            {errors.country && errors.country.message}
                                                                        </FormErrorMessage>
                                                                    </FormControl>
                                                                    <FormControl mb='5' isInvalid={errors.street}>
                                                                        <FormLabel
                                                                            display='flex'
                                                                            ms='4px'
                                                                            fontSize='sm'
                                                                            fontWeight='500'
                                                                            color={textColor}
                                                                            htmlFor='street'
                                                                        >
                                                                            Strasse und Hausnummer<Text
                                                                            color={brandStars}>*</Text>
                                                                        </FormLabel>
                                                                        <Input
                                                                            id='street'
                                                                            fontSize='sm'
                                                                            type='text'
                                                                            fontWeight='500'
                                                                            size='lg'
                                                                            {...register('street', {
                                                                                required: 'Bitte geben Sie die Strasse und Hausnummer der Unterkunft an',
                                                                            })}
                                                                        />
                                                                        <FormErrorMessage>
                                                                            {errors.street && errors.street.message}
                                                                        </FormErrorMessage>
                                                                    </FormControl>
                                                                    <FormControl mb='5'>
                                                                        <FormLabel
                                                                            display='flex'
                                                                            ms='4px'
                                                                            fontSize='sm'
                                                                            fontWeight='500'
                                                                            color={textColor}
                                                                            htmlFor='zip'
                                                                        >
                                                                            PLZ
                                                                        </FormLabel>
                                                                        <Input
                                                                            id='zip'
                                                                            fontSize='sm'
                                                                            type='text'
                                                                            fontWeight='500'
                                                                            size='lg'
                                                                            {...register('zip')}
                                                                        />
                                                                    </FormControl>
                                                                    <FormControl mb='5' isInvalid={errors.place}>
                                                                        <FormLabel
                                                                            display='flex'
                                                                            ms='4px'
                                                                            fontSize='sm'
                                                                            fontWeight='500'
                                                                            color={textColor}
                                                                            htmlFor='place'
                                                                        >
                                                                            Stadt/Ort<Text
                                                                            color={brandStars}>*</Text>
                                                                        </FormLabel>
                                                                        <Input
                                                                            id='place'
                                                                            fontSize='sm'
                                                                            type='text'
                                                                            fontWeight='500'
                                                                            size='lg'
                                                                            {...register('place', {
                                                                                required: 'Bitte geben Sie die Stadt / den Ort der Unterkunft an',
                                                                            })}
                                                                        />
                                                                        <FormErrorMessage>
                                                                            {errors.place && errors.place.message}
                                                                        </FormErrorMessage>
                                                                    </FormControl>
                                                                </Box> : ''}
                                                            <Mapbox
                                                                onAddressChange={handleAddressChange}
                                                            />
                                                        </Card>
                                                    </SlideFade> :
                                                    currentStep === 7 ?
                                                        <Card>
                                                            <Heading mb={5} size='xl'>Ist diese Adresse korrekt und
                                                                wird sie auf der Karte richtig angezeigt?</Heading>
                                                            <Text mb={5}>Du kannst den Pin auf der Karte verschieben
                                                                um die richtige Position deiner Unterkunft zu
                                                                markieren</Text>
                                                            <MapboxMoveable address={formattedAddress}
                                                                            onCoordinatesChange={handleCoordinatesChange}/>
                                                        </Card> :
                                                        currentStep === 8 ?
                                                            <SlideFade in={isFading} offsetY='20px'>
                                                                <Card>
                                                                    <Heading mb={5} size='xl'>Fast geschafft!
                                                                        Bestätige nun die Buchung des
                                                                        Inserats.</Heading>
                                                                    <FormControl display='flex'
                                                                                 alignItems='center'>
                                                                        <Checkbox
                                                                            id='optin'
                                                                            colorScheme='brandScheme'
                                                                            me='10px'
                                                                            size={'lg'}
                                                                            {...register('optin', {
                                                                                required: 'Bitte bestätgen Sie die Buchung des Inserats',
                                                                            })}
                                                                        />
                                                                        <FormLabel
                                                                            htmlFor='optin'
                                                                            mb='0'
                                                                            fontWeight='normal'
                                                                            color={textColor}
                                                                            fontSize='lg'>
                                                                            {!user?.has_rentals ?
                                                                                `Hiermit buche ich kostenpflichtig das Inserat für 180 €${user.has_vat ? ' pro Jahr zzgl. MwSt.' : ' pro Jahr'}. Du kannst dein Objekt jederzeit bearbeiten. Das Objekt wird online geschaltet, sobald die Rechnung überwiesen wurde.`
                                                                                : `Hiermit buche ich kostenpflichtig ein weiteres Inserat für 120 €${user.has_vat ? ' pro Jahr zzgl. MwSt.' : ' pro Jahr'}. Du kannst dein Objekt jederzeit bearbeiten. Das Objekt wird online geschaltet, sobald die Rechnung überwiesen wurde.`
                                                                            }
                                                                        </FormLabel>
                                                                        <FormErrorMessage>
                                                                            {errors.optin && errors.optin.message}
                                                                        </FormErrorMessage>
                                                                    </FormControl>
                                                                    <Heading size="md" mt={5}
                                                                             mb={5}>Unser Angebot</Heading>
                                                                    <FormControl display='flex'
                                                                                 alignItems='center'>
                                                                        <Checkbox
                                                                            id='optin_extended'
                                                                            colorScheme='brandScheme'
                                                                            me='10px'
                                                                            iconSize={'lg'}
                                                                            {...register('optin_extended')}
                                                                        />
                                                                        <FormLabel
                                                                            htmlFor='optin_extended'
                                                                            mb='0'
                                                                            fontWeight='normal'
                                                                            color={textColor}
                                                                            fontSize='lg'>
                                                                            Ich buche das Inserat für 2 Jahre und erhalte damit einen exklusiven Rabatt von 10%
                                                                        </FormLabel>
                                                                        <svg height="25" width="25"
                                                                             viewBox="0 0 512 512"
                                                                             xmlns="http://www.w3.org/2000/svg">
                                                                            <path fill="#FF5023"
                                                                                  d="M256 512c-2.954 0-5.737-1.144-7.835-3.221l-47.332-46.876-64.437 16.927c-.946.25-1.903.376-2.857.376-4.984 0-9.387-3.374-10.709-8.205l-17.559-64.27L41 389.171c-5.86-1.601-9.372-7.687-7.829-13.566l16.927-64.436-47.001-48.518c-4.272-4.319-4.272-11.349.002-15.67l46.875-47.331-16.927-64.437c-1.544-5.878 1.969-11.964 7.829-13.566l64.27-17.559 17.56-64.27c1.32-4.828 5.728-8.201 10.717-8.201.952 0 1.909.125 2.843.371l64.442 16.927 47.333-46.877C250.265 1.145 253.048 0 255.999 0s5.734 1.145 7.835 3.223l47.332 46.876 64.437-16.927c.939-.247 1.9-.372 2.854-.372 4.99 0 9.394 3.371 10.711 8.199l17.56 64.272 64.27 17.56c5.86 1.601 9.372 7.687 7.829 13.566l-16.927 64.436 46.877 47.333c4.272 4.319 4.272 11.349-.002 15.67L461.9 311.168l16.927 64.437c1.544 5.878-1.969 11.964-7.829 13.566l-64.27 17.56-17.56 64.27c-1.318 4.832-5.719 8.205-10.703 8.205-.955 0-1.916-.126-2.854-.374l-64.445-16.928-47.333 46.877c-2.101 2.077-4.884 3.221-7.834 3.221z"/>
                                                                            <path fill="#CD2A00"
                                                                                  d="M461.902 200.834l16.927-64.436c1.544-5.879-1.969-11.965-7.829-13.566l-64.27-17.56L389.169 41c-1.317-4.827-5.721-8.199-10.711-8.199-.954 0-1.914.125-2.854.372L311.167 50.1 263.835 3.223C261.735 1.145 259.34 0 256.001 0v512c3.339 0 5.736-1.144 7.834-3.221l47.333-46.877 64.445 16.928c.939.248 1.899.374 2.854.374 4.984 0 9.385-3.374 10.703-8.205l17.56-64.27 64.27-17.56c5.86-1.601 9.372-7.687 7.829-13.566l-16.927-64.437 46.875-47.331c4.274-4.321 4.274-11.352.002-15.67L461.902 200.834z"/>
                                                                            <path fill="#FFFFFF"
                                                                                  d="M189.217 244.871c-30.684 0-55.652-24.968-55.652-55.652s24.968-55.652 55.652-55.652 55.652 24.968 55.652 55.652-24.968 55.652-55.652 55.652zm0-77.913c-12.277 0-22.261 9.984-22.261 22.261s9.984 22.261 22.261 22.261c12.277 0 22.261-9.984 22.261-22.261s-9.984-22.261-22.261-22.261z"/>
                                                                            <path fill="#D1E7F8"
                                                                                  d="M322.782 378.435c-30.684 0-55.652-24.968-55.652-55.652s24.968-55.652 55.652-55.652 55.652 24.968 55.652 55.652-24.968 55.652-55.652 55.652zm0-77.913c-12.277 0-22.261 9.984-22.261 22.261 0 12.277 9.984 22.261 22.261 22.261 12.277 0 22.261-9.984 22.261-22.261 0-12.277-9.984-22.261-22.261-22.261z"/>
                                                                            <path fill="#FFFFFF"
                                                                                  d="M178.087 350.609c-4.272 0-8.544-1.631-11.804-4.892-6.521-6.516-6.521-17.092 0-23.609L322.108 166.285c6.521-6.521 17.087-6.521 23.609 0 6.521 6.516 6.521 17.092 0 23.609L189.892 345.718c-3.261 3.261-7.532 4.891-11.805 4.891z"/>
                                                                            <path fill="#D1E7F8"
                                                                                  d="M322.108 166.285l-66.108 66.109v47.217l89.716-89.716c6.521-6.516 6.521-17.092 0-23.609-6.521-6.521-17.087-6.521-23.608 0z"/>
                                                                        </svg>
                                                                    </FormControl>
                                                                </Card>
                                                                <Card mt={5}>
                                                                    <Heading mb={5} size="md">Dein Inserat erscheint
                                                                        auf
                                                                        den Portalen:</Heading>
                                                                    <List spacing={3}>
                                                                        {user?.source_url &&
                                                                            <ListItem display="flex"
                                                                                      alignItems="center">
                                                                                <ListIcon as={MdCheckCircle}
                                                                                          color='green.500'
                                                                                          boxSize="2em"/>
                                                                                <Link color='brand.500'
                                                                                      href={user.source_url}
                                                                                      target={'_blank'}>{user.source_url}</Link>
                                                                            </ListItem>}
                                                                        <ListItem display="flex"
                                                                                  alignItems="center">
                                                                            <ListIcon as={MdCheckCircle}
                                                                                      color='green.500'
                                                                                      boxSize="2em"/>
                                                                            <Link color='brand.500'
                                                                                  href="https://ferienhausurlaub.com/"
                                                                                  target={'_blank'}>https://ferienhausurlaub.com</Link>
                                                                        </ListItem>
                                                                    </List>
                                                                    <Text mt={5}>Um die bestmögliche Sichtbarkeit
                                                                        deines Inserats zu gewährleisten, nutzen wir
                                                                        unser Netzwerk aus erstklassig platzierten
                                                                        Unterkunfts-Webseiten. Dein Inserat wird
                                                                        sowohl orts- als auch themenabhängig auf
                                                                        zusätzlichen, thematisch passenden Portalen
                                                                        präsentiert. Das vollständige Portfolio
                                                                        findest du <Link color='brand.500'
                                                                                         target={'_blank'}
                                                                                         href="https://ferienhaus.online/unterkunft-vermieten/">hier</Link>.</Text></Card></SlideFade> : ''
                        }
                        <RentalPreview
                            isOpen={isOpen}
                            onClose={onClose}
                            data={previewData}
                        />
                    </Box>
                    <Box mt={{base: "0", lg: '50px'}} position={{base: "relative", lg: 'fixed'}} bottom='0'
                         left={{base: "0", lg: '320px'}} right={{base: "0", lg: '20px'}}>
                        <Card borderBottomLeftRadius={0} borderBottomRightRadius={0}>
                            <Progress isAnimated={true} width={'100%'}
                                      value={(currentStep + 1) / steps.length * 100}
                                      size='sm'
                                      colorScheme='brand'/>
                            <Flex direction={{base: "column", lg: 'row'}}>
                                {currentStep !== 0 &&
                                    <Button
                                        fontSize='sm'
                                        variant='outline'
                                        fontWeight='500'
                                        w={{base: "100%", lg: '100px'}}
                                        h='50'
                                        mt='24px'
                                        mr='auto'
                                        isLoading={isLoading}
                                        onClick={handlePrevious}
                                    >
                                        Zurück
                                    </Button>
                                }
                                <Box>
                                    <Button
                                        fontSize='sm'
                                        variant='outline'
                                        fontWeight='500'
                                        h='50'
                                        mt='24px'
                                        ml='15px'
                                        onClick={handleOpenPreview}
                                    >
                                        <Icon
                                            transition='0.2s linear'
                                            w='20px'
                                            h='20px'
                                            as={IoEye}
                                            mr={2}
                                        /> Suchergebnis-Vorschau
                                    </Button>
                                    <Button
                                        fontSize='sm'
                                        variant='outline'
                                        fontWeight='500'
                                        h='50'
                                        mt='24px'
                                        ml='15px'
                                        isLoading={isLoading}
                                        onClick={handleExit}
                                    >
                                        <Icon
                                            transition='0.2s linear'
                                            w='20px'
                                            h='20px'
                                            as={IoSave}
                                            mr={2}
                                        /> Zwischenspeichern
                                    </Button>
                                </Box>
                                {currentStep === steps.length - 1 ?
                                    <Button
                                        fontSize='sm'
                                        variant='brand'
                                        fontWeight='500'
                                        w={{base: "100%", lg: '300px'}}
                                        h='50'
                                        mt='24px'
                                        ml='auto'
                                        isDisabled={!stepIsValid}
                                        isLoading={isSubmitting}
                                        type='submit'
                                    >
                                        Inserat jetzt einstellen
                                    </Button> :
                                    <Button
                                        fontSize='sm'
                                        variant='brand'
                                        fontWeight='500'
                                        w={{base: "100%", lg: '100px'}}
                                        h='50'
                                        mt='24px'
                                        ml='auto'
                                        isDisabled={!stepIsValid}
                                        isLoading={isLoading}
                                        onClick={handleNext}
                                    >
                                        Weiter
                                    </Button>
                                }
                            </Flex>
                        </Card>
                    </Box>
                </form>
            }
        </Box>
    );
}
